import { createSignal } from 'solid-js'
import type { Locale } from '../types'
import type { Translations } from './en'

const [loadedDictionaries, setLoadedDictionaries] = createSignal<
  Partial<Record<Locale, Translations>>
>({})

const [dictionaryLoadFailed, setDictionaryLoadFailed] = createSignal(false)

export { loadedDictionaries, dictionaryLoadFailed }

const loaders: Record<Locale, () => Promise<Translations>> = {
  en: () => import('./en').then((m) => m.en),
  fr: () => import('./fr').then((m) => m.fr),
  es: () => import('./es').then((m) => m.es),
  pt: () => import('./pt').then((m) => m.pt),
}

const pending = new Map<Locale, Promise<Translations>>()

const MAX_ATTEMPTS = 3

export function ensureDictionary(locale: Locale): Promise<Translations> {
  const loaded = loadedDictionaries()[locale]
  if (loaded) {
    return Promise.resolve(loaded)
  }
  const inFlight = pending.get(locale)
  if (inFlight) {
    return inFlight
  }

  const attemptLoad = async (): Promise<Translations> => {
    for (let attempt = 1; ; attempt++) {
      try {
        return await loaders[locale]()
      } catch (err) {
        if (attempt >= MAX_ATTEMPTS) {
          throw err
        }
        await new Promise((resolve) => setTimeout(resolve, attempt * 500))
      }
    }
  }

  const load = attemptLoad()
    .then((dict) => {
      setLoadedDictionaries((prev) => ({ ...prev, [locale]: dict }))
      return dict
    })
    .catch((err) => {
      console.error(`i18n: failed to load "${locale}" catalog`, err)
      setDictionaryLoadFailed(true)
      throw err
    })
    .finally(() => {
      pending.delete(locale)
    })
  pending.set(locale, load)
  return load
}

export type { TranslationKey, Translations } from './en'
